import React, { useState, useEffect } from 'react';
import { Plus, GripVertical, Pencil, Trash2, MapPin } from 'lucide-react';
import { gachaIslandsApi, upgradeIslandsApi, PLACEHOLDER_IMAGE } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const IslandManager = ({ onSelectIsland, selectedIslandId, type = 'gacha' }) => {
    const { user } = useAuth();
    const [islands, setIslands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingIsland, setEditingIsland] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [islandToDelete, setIslandToDelete] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', image_url: '' });
    const [draggedItem, setDraggedItem] = useState(null);

    // Select the correct API based on type
    const api = type === 'upgrade' ? upgradeIslandsApi : gachaIslandsApi;

    useEffect(() => {
        loadIslands();
    }, [type]);

    const loadIslands = async () => {
        try {
            const response = await api.getAll();
            const data = response.data;
            const islandsArray = Array.isArray(data) ? data : (data.items || data || []);
            setIslands(islandsArray);
            // Auto-select first island if none selected
            if (islandsArray.length > 0 && !selectedIslandId) {
                onSelectIsland(islandsArray[0]);
            }
        } catch (error) {
            console.error('Failed to load islands:', error);
            toast.error('Fehler beim Laden der Inseln');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!formData.name.trim()) return;
        try {
            await api.create({
                name: formData.name.trim(),
                description: formData.description || '',
                image_url: formData.image_url || null,
            }, user?.discord_id, user?.username);
            setFormData({ name: '', description: '', image_url: '' });
            setModalOpen(false);
            loadIslands();
        } catch (error) {
            console.error('Failed to create island:', error);
            toast.error('Fehler beim Erstellen der Insel');
        }
    };

    const handleUpdate = async () => {
        if (!formData.name.trim() || !editingIsland) return;
        try {
            await api.update(editingIsland.id, {
                name: formData.name.trim(),
                description: formData.description || '',
                image_url: formData.image_url || null,
            }, user?.discord_id, user?.username);
            setFormData({ name: '', description: '', image_url: '' });
            setEditingIsland(null);
            setModalOpen(false);
            toast.success('Insel aktualisiert');
            loadIslands();
        } catch (error) {
            console.error('Failed to update island:', error);
            toast.error('Fehler beim Aktualisieren der Insel');
        }
    };

    const handleDelete = async () => {
        if (!islandToDelete) return;
        try {
            await api.delete(islandToDelete.id, user?.discord_id, user?.username);
            toast.success('Insel und zugehörige Daten gelöscht');
            setDeleteModalOpen(false);
            setIslandToDelete(null);
            if (selectedIslandId === islandToDelete.id) {
                onSelectIsland(null);
            }
            loadIslands();
        } catch (error) {
            console.error('Failed to delete island:', error);
            toast.error('Fehler beim Löschen der Insel');
        }
    };

    const openEditModal = (island, e) => {
        e.stopPropagation();
        setEditingIsland(island);
        setFormData({ 
            name: island.name, 
            description: island.description || '', 
            image_url: island.image_url || '' 
        });
        setModalOpen(true);
    };

    const openCreateModal = () => {
        setEditingIsland(null);
        setFormData({ name: '', description: '', image_url: '' });
        setModalOpen(true);
    };

    const openDeleteModal = (island, e) => {
        e.stopPropagation();
        setIslandToDelete(island);
        setDeleteModalOpen(true);
    };

    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItem === null || draggedItem === index) return;

        const newIslands = [...islands];
        const draggedIsland = newIslands[draggedItem];
        newIslands.splice(draggedItem, 1);
        newIslands.splice(index, 0, draggedIsland);
        
        setIslands(newIslands);
        setDraggedItem(index);
    };

    const handleDragEnd = async () => {
        if (draggedItem === null) return;
        
        const updates = islands.map((island, index) => ({
            id: island.id,
            order: index
        }));
        
        try {
            await api.reorder(updates);
        } catch (error) {
            console.error('Failed to reorder:', error);
            loadIslands();
        }
        
        setDraggedItem(null);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-['Rajdhani'] font-semibold text-lg text-white">
                        Inseln verwalten
                    </h3>
                    <p className="text-xs text-gray-500">Wähle eine Insel oder erstelle eine neue</p>
                </div>
                <Button
                    onClick={openCreateModal}
                    size="sm"
                    data-testid="create-island-btn"
                    className="bg-cyan-500 hover:bg-cyan-400 text-black"
                >
                    <Plus size={16} className="mr-1" />
                    Neue Insel
                </Button>
            </div>

            {/* Islands Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {loading ? (
                    <div className="col-span-full p-8 text-center text-gray-500">Laden...</div>
                ) : islands.length === 0 ? (
                    <div className="col-span-full p-8 text-center text-gray-500 border border-dashed border-white/10 rounded-xl">
                        Noch keine Inseln vorhanden
                    </div>
                ) : (
                    islands.map((island, index) => (
                        <div
                            key={island.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            onClick={() => onSelectIsland(island)}
                            data-testid={`island-card-${island.id}`}
                            className={cn(
                                "relative group cursor-pointer rounded-xl overflow-hidden border transition-all",
                                selectedIslandId === island.id
                                    ? "border-cyan-500 bg-cyan-500/10 ring-1 ring-cyan-500/30"
                                    : "border-white/10 bg-white/5 hover:border-white/20",
                                draggedItem === index && "opacity-50"
                            )}
                        >
                            {/* Image */}
                            <div className="h-24 bg-gradient-to-br from-cyan-900/30 to-purple-900/30 overflow-hidden">
                                {island.image_url ? (
                                    <img
                                        src={island.image_url}
                                        alt={island.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <MapPin size={32} className="text-white/20" />
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-3">
                                <h4 className="font-['Rajdhani'] font-semibold text-white truncate">
                                    {island.name}
                                </h4>
                                {island.description && (
                                    <p className="text-xs text-gray-500 truncate mt-1">
                                        {island.description}
                                    </p>
                                )}
                            </div>

                            {/* Drag Handle */}
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <GripVertical size={16} className="text-white/50" />
                            </div>

                            {/* Actions */}
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => openEditModal(island, e)}
                                    data-testid={`edit-island-${island.id}`}
                                    className="h-7 w-7 bg-black/50 hover:bg-cyan-500/20 text-white hover:text-cyan-400"
                                >
                                    <Pencil size={12} />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => openDeleteModal(island, e)}
                                    data-testid={`delete-island-${island.id}`}
                                    className="h-7 w-7 bg-black/50 hover:bg-red-500/20 text-white hover:text-red-400"
                                >
                                    <Trash2 size={12} />
                                </Button>
                            </div>

                            {/* Selected Indicator */}
                            {selectedIslandId === island.id && (
                                <div className="absolute bottom-0 left-0 right-0 h-1 bg-cyan-500" />
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-xl text-white">
                            {editingIsland ? 'Insel bearbeiten' : 'Neue Insel erstellen'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-gray-400">Name *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Inselname"
                                data-testid="island-name-input"
                                className="bg-black/40 border-white/10"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Beschreibung</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Optionale Beschreibung..."
                                rows={2}
                                className="bg-black/40 border-white/10 resize-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Bild URL</Label>
                            <Input
                                value={formData.image_url}
                                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                placeholder="https://..."
                                className="bg-black/40 border-white/10"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={editingIsland ? handleUpdate : handleCreate}
                            disabled={!formData.name.trim()}
                            data-testid="save-island-btn"
                            className="bg-cyan-500 hover:bg-cyan-400 text-black"
                        >
                            {editingIsland ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="max-w-sm bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-xl text-red-400">
                            Insel löschen?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-gray-400">
                        Möchtest du <span className="text-white font-semibold">"{islandToDelete?.name}"</span> wirklich löschen? 
                        <span className="text-red-400"> Alle zugehörigen Gachas und Upgrades werden ebenfalls gelöscht!</span>
                    </p>
                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleDelete}
                            data-testid="confirm-delete-island"
                            className="bg-red-500 hover:bg-red-400 text-white"
                        >
                            Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
